/**
 * @fileoverview `useRateLimit` — the ONE client-side attempt throttle for the ecosystem.
 *
 * Counts attempts inside a sliding `windowMs`; when `maxAttempts` of them land inside the window
 * it LOCKS for `lockoutMs`, counting `timeUntilReset` down so the surface can say how long is
 * left, then unlocks itself with a clean slate.
 *
 * This is defence-in-depth and a UX affordance, NOT the security boundary. The server's own rate
 * limiting is the boundary; this stops a user collecting a 429 mid-flow and gives them a number to
 * wait on. A client throttle is trivially bypassed and is never relied on for protection.
 *
 * ## Two ways to spend the budget — pick ONE per instance
 *
 * | call | what counts | for |
 * | ---- | ----------- | --- |
 * | `checkLimit()` | EVERY submission | a form whose every send has a cost — a code sent, a device registered |
 * | `recordFailure()` / `recordSuccess()` | only FAILED attempts; a success forgets them | a credential check, where a correct sign-in must not spend the budget |
 *
 * Both paths feed the same ledger and the same lockout; they differ only in WHICH events the
 * caller reports. A failure-counting surface gates its submit on `isLimited` directly.
 *
 * ## Why there is one engine and not two (Q-UMB-20, 2026-09-24)
 *
 * Until this change the fleet carried two: this package's (count every submission, cool down for
 * `windowMs`, attempts never aged out) and a module-private `useLoginRateLimit` inside
 * `@puffin-core/login-shell` (count failures inside a window, reset on success, lock for
 * `lockoutMs`). The login engine's semantics were the better ones — a window the attempts age out
 * of is what "N attempts per window" means, and it is how the backend limiter it mirrors behaves —
 * so they were promoted HERE and the login engine was deleted. `lockoutMs` defaults to `windowMs`,
 * so a caller that names only `windowMs` keeps the cooldown length it always had.
 *
 * @example
 * // Every submission counts:
 * const rateLimit = useRateLimit({ maxAttempts: 5, windowMs: 60000 });
 * const handleSubmit = (event) => {
 *   event.preventDefault();
 *   if (!rateLimit.checkLimit()) return; // refused — <RateLimitNotice> says why and for how long
 *   submit();
 * };
 *
 * @example
 * // Only failures count:
 * const rateLimit = useRateLimit(LOGIN_RATE_LIMIT_CONFIG);
 * if (rateLimit.isLimited) return;
 * try { await signIn(); rateLimit.recordSuccess(); } catch { rateLimit.recordFailure(); }
 *
 * @module @puffin-core/form-security/useRateLimit
 */

import { useState, useCallback, useEffect, useRef } from 'react';

// No isMountedRef ceremony — React 19 makes post-unmount setState a silent no-op; see
// GUIDES/shared/standards/testing/react-19-unmount-semantics.md. The refs below hold live timer
// handles and the synchronous attempt ledger, not a mount flag.

/**
 * Default policy.
 *
 * `lockoutMs` is deliberately absent: it defaults to the caller's `windowMs`, so a caller that
 * names only a window keeps the cooldown length the pre-consolidation engine gave it.
 *
 * @type {Readonly<{maxAttempts: number, windowMs: number}>}
 */
const DEFAULT_POLICY = Object.freeze({
  maxAttempts: 5,
  windowMs: 60000, // 1 minute
});

/**
 * How often the countdown is re-published while locked.
 *
 * One second, because every surface renders WHOLE seconds and a lockout can be fifteen minutes: the
 * pre-consolidation 100 ms tick re-rendered a login form 9,000 times to change the visible number
 * 900 times. Each tick recomputes the remainder from the lockout's end rather than decrementing, so
 * timer drift can never accumulate into the displayed value.
 */
const TICK_MS = 1000;

/** The ledger before anything has happened. */
const EMPTY_LEDGER = Object.freeze({ attempts: Object.freeze([]), lockedUntil: null });

/**
 * Validate one policy number, throwing a message that names the option.
 *
 * 🔴 A ceiling of zero or less used to be NORMALISED to zero, which left a form limited from its
 * first render with `timeUntilReset` 0 and no timer to ever end it — a submit button that could
 * never be pressed and a countdown frozen at nothing. No caller ever passed one. A bad policy is a
 * programming error, and it is reported as one here rather than rendered as a dead form.
 *
 * @param {string} name - the option name, for the message
 * @param {unknown} value - the supplied value
 * @returns {number} the value, proven a positive integer
 * @throws {TypeError} when the value is not a positive integer
 */
function requirePositiveInteger(name, value) {
  // `typeof` first: `Number.isInteger` already refuses every non-number, but it is no type guard, so without it
  // `value` stays `unknown` for the comparison and the return.
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    throw new TypeError(
      `useRateLimit: \`${name}\` must be a positive integer, received ${String(value)}`,
    );
  }
  return value;
}

/**
 * Resolve the caller's options against the defaults and validate every number.
 *
 * @param {{maxAttempts?: number, windowMs?: number, lockoutMs?: number}} options
 * @returns {{maxAttempts: number, windowMs: number, lockoutMs: number}} the resolved policy
 * @throws {TypeError} on any option that is not a positive integer
 */
export function resolveRateLimitPolicy(options = {}) {
  const maxAttempts = requirePositiveInteger(
    'maxAttempts',
    options.maxAttempts ?? DEFAULT_POLICY.maxAttempts,
  );
  const windowMs = requirePositiveInteger('windowMs', options.windowMs ?? DEFAULT_POLICY.windowMs);
  const lockoutMs = requirePositiveInteger('lockoutMs', options.lockoutMs ?? windowMs);
  return { maxAttempts, windowMs, lockoutMs };
}

/**
 * Pure ledger step: record one attempt at `now`.
 *
 * Attempts older than `windowMs` age out first, then the new one is appended. When the windowed
 * count reaches `maxAttempts`, the ledger locks until `now + lockoutMs`. A ledger that is already
 * locked is returned unchanged — a refused attempt neither counts nor extends the lockout, so a
 * held-down button cannot run the count away.
 *
 * @param {{attempts: ReadonlyArray<number>, lockedUntil: number|null}} ledger
 * @param {number} now - epoch milliseconds
 * @param {{maxAttempts: number, windowMs: number, lockoutMs: number}} policy
 * @returns {{attempts: ReadonlyArray<number>, lockedUntil: number|null}} the next ledger
 */
export function recordAttempt(ledger, now, policy) {
  if (ledger.lockedUntil !== null) return ledger;
  const attempts = [...ledger.attempts.filter((at) => at > now - policy.windowMs), now];
  const lockedUntil = attempts.length >= policy.maxAttempts ? now + policy.lockoutMs : null;
  return { attempts, lockedUntil };
}

/**
 * Clear both timer handles and null them, so a later clear is a no-op rather than a double-clear.
 *
 * @param {{timeout: ReturnType<typeof setTimeout>|null, interval: ReturnType<typeof setInterval>|null}} timers
 * @returns {void}
 */
function clearAllTimers(timers) {
  if (timers.timeout) {
    clearTimeout(timers.timeout);
    timers.timeout = null;
  }
  if (timers.interval) {
    clearInterval(timers.interval);
    timers.interval = null;
  }
}

/**
 * The rendered snapshot of a ledger at `now`.
 *
 * @param {{attempts: ReadonlyArray<number>, lockedUntil: number|null}} ledger
 * @param {number} now - epoch milliseconds
 * @returns {{attemptCount: number, lockedUntil: number|null, timeUntilReset: number}}
 */
function snapshotOf(ledger, now) {
  return {
    attemptCount: ledger.attempts.length,
    lockedUntil: ledger.lockedUntil,
    timeUntilReset: ledger.lockedUntil === null ? 0 : Math.max(0, ledger.lockedUntil - now),
  };
}

/**
 * Client-side attempt throttling for a form or an action.
 *
 * @param {object} [options] - the policy
 * @param {number} [options.maxAttempts=5] - attempts allowed inside the window before it locks
 * @param {number} [options.windowMs=60000] - the sliding window attempts are counted in
 * @param {number} [options.lockoutMs=windowMs] - how long the lock lasts once the ceiling is hit
 * @returns {{
 *   isLimited: boolean,
 *   attemptCount: number,
 *   remainingAttempts: number,
 *   maxAttempts: number,
 *   timeUntilReset: number,
 *   lockedUntil: number|null,
 *   checkLimit: () => boolean,
 *   recordFailure: () => number|null,
 *   recordSuccess: () => void,
 *   lockFor: (durationMs: number) => void,
 * }} state and controls
 * @throws {TypeError} when any policy option is not a positive integer
 */
export function useRateLimit(options = {}) {
  const { maxAttempts, windowMs, lockoutMs } = resolveRateLimitPolicy(options);

  // The ledger is the SYNCHRONOUS truth: two submits inside one render must see each other's
  // attempt, which a value read from render state cannot guarantee. `view` is what renders.
  const ledgerRef = useRef(EMPTY_LEDGER);
  const timersRef = useRef({ timeout: null, interval: null });
  const [view, setView] = useState(() => snapshotOf(EMPTY_LEDGER, 0));

  const publish = useCallback((ledger) => {
    ledgerRef.current = ledger;
    setView(snapshotOf(ledger, Date.now()));
  }, []);

  /** Forget every attempt, end any lockout, and stop both timers. */
  const clearLedger = useCallback(() => {
    clearAllTimers(timersRef.current);
    publish(EMPTY_LEDGER);
  }, [publish]);

  /**
   * Arm the unlock and the countdown for a lockout lasting `durationMs` from now.
   *
   * @param {number} durationMs
   * @returns {void}
   */
  const armLockout = useCallback(
    (durationMs) => {
      clearAllTimers(timersRef.current);
      // The timeout owns the unlock AND the interval's teardown (`clearLedger` clears both), so the
      // tick never needs a self-clear branch — the one payday-web's pre-consolidation copy carried
      // and pos-web's had already proved unreachable.
      timersRef.current.timeout = setTimeout(clearLedger, durationMs);
      timersRef.current.interval = setInterval(() => {
        setView(snapshotOf(ledgerRef.current, Date.now()));
      }, TICK_MS);
    },
    [clearLedger],
  );

  /**
   * Record one attempt; when it starts a lockout, arm the unlock and the countdown.
   *
   * @returns {number|null} the lockout's end (epoch ms) when this attempt started one, else null
   */
  const record = useCallback(() => {
    const previous = ledgerRef.current;
    const next = recordAttempt(previous, Date.now(), { maxAttempts, windowMs, lockoutMs });
    if (next === previous) return null;
    publish(next);
    if (next.lockedUntil === null) return null;
    armLockout(lockoutMs);
    return next.lockedUntil;
  }, [maxAttempts, windowMs, lockoutMs, publish, armLockout]);

  /**
   * Lock for `durationMs` because the SERVER refused as rate-limited (a 429's `details.retryAfter`),
   * so the one throttle — and the one `<RateLimitNotice>` countdown — speak for both sides
   * (LIFE-SHORTLISTED-001 S3). A lockout already running longer is kept: the later end wins.
   *
   * @param {number} durationMs - positive integer milliseconds
   * @returns {void}
   * @throws {TypeError} when `durationMs` is not a positive integer
   */
  const lockFor = useCallback(
    (durationMs) => {
      if (!Number.isInteger(durationMs) || durationMs <= 0) {
        throw new TypeError(
          `useRateLimit: lockFor(durationMs) needs a positive integer, received ${String(durationMs)}`,
        );
      }
      const now = Date.now();
      const current = ledgerRef.current;
      if (current.lockedUntil !== null && current.lockedUntil >= now + durationMs) return;
      publish({ attempts: current.attempts, lockedUntil: now + durationMs });
      armLockout(durationMs);
    },
    [publish, armLockout],
  );

  /**
   * Count a SUBMISSION and report whether it may proceed.
   *
   * @returns {boolean} true when the attempt may proceed; false while locked
   */
  const checkLimit = useCallback(() => {
    if (ledgerRef.current.lockedUntil !== null) return false;
    record();
    return true;
  }, [record]);

  // Cleanup on unmount — the timers outlive the component otherwise.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      clearAllTimers(timers);
    };
  }, []);

  return {
    isLimited: view.lockedUntil !== null,
    attemptCount: view.attemptCount,
    remainingAttempts: Math.max(0, maxAttempts - view.attemptCount),
    maxAttempts,
    timeUntilReset: view.timeUntilReset,
    lockedUntil: view.lockedUntil,
    checkLimit,
    // A FAILED attempt counts; one refused while locked is ignored, so a lockout is never extended
    // by the attempts it refused. Returns the lockout's end when this failure started one.
    recordFailure: record,
    // A success forgets every recorded attempt, so a correct attempt never spends the budget.
    recordSuccess: clearLedger,
    // A lockout the server imposed (see lockFor).
    lockFor,
  };
}

export default useRateLimit;
